/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import BusinessIcon from "@material-ui/icons/Business";
import DescriptionIcon from "@material-ui/icons/Description";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import GroupIcon from "@material-ui/icons/Group";
import ListAltIcon from "@material-ui/icons/ListAlt";
import SubscriptionsIcon from "@material-ui/icons/Subscriptions";
import {
    NavBarLinkIconV1,
    NavBarLinkTextV1,
    NavBarLinkV1,
    NavBarPrimaryContainerV1,
    NavBarSecondaryContainerV1,
    NavBarV1,
} from "@startree-ui/platform-ui";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
    AppRoute,
    getLogoutPath,
    getOrganizationsPath,
} from "../../utils/routes/routes.util";
import { isCloudV2User } from "../../utils/user/user.util";
import { AboutDialogNavBarButton } from "../about-dialog-nav-bar-button/about-dialog-nav-bar-button.component";
import { useAuthProvider } from "../auth-provider-loader/auth-provider-loader.component";
import { UserDialogNavBarButton } from "../user-dialog-nav-bar-button/user-dialog-nav-bar-button.component";
import { useAppNavBarStyles } from "./app-nav-bar.styles";

export const AppNavBar: FunctionComponent = () => {
    const { isAuthenticated, user: auth0User } = useAuthProvider();
    const location = useLocation();
    const { t } = useTranslation();
    const appNavBarButtonClasses = useAppNavBarStyles();

    const isRouteCurrent = (route: string): boolean => {
        return location.pathname.indexOf(route) === 0;
    };

    return (
        <NavBarV1
            maximizeLabel={t("label.maximize")}
            minimizeLabel={t("label.minimize")}
        >
            <NavBarPrimaryContainerV1>
                {/* Organizations */}
                {isAuthenticated && !isCloudV2User(auth0User) && (
                    <NavBarLinkV1
                        href={getOrganizationsPath()}
                        selected={isRouteCurrent(AppRoute.ORGANIZATIONS)}
                    >
                        <NavBarLinkIconV1>
                            <BusinessIcon />
                        </NavBarLinkIconV1>

                        <NavBarLinkTextV1>
                            {t("label.organizations")}
                        </NavBarLinkTextV1>
                    </NavBarLinkV1>
                )}
            </NavBarPrimaryContainerV1>

            <NavBarSecondaryContainerV1>
                {isAuthenticated && !isCloudV2User(auth0User) && (
                    <>
                        {/* Documentation */}
                        <NavBarLinkV1
                            externalLink
                            href={t("url.documentation")}
                            target="_blank"
                        >
                            <NavBarLinkIconV1>
                                <DescriptionIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1>
                                {t("label.documentation")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>

                        {/* Join Slack Community */}
                        <NavBarLinkV1
                            externalLink
                            href={t("url.join-slack-community")}
                            target="_blank"
                        >
                            <NavBarLinkIconV1>
                                <GroupIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1
                                className={appNavBarButtonClasses.joinSlack}
                            >
                                {t("label.join-slack-community")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>

                        {/* Videos */}
                        <NavBarLinkV1
                            externalLink
                            href={t("url.startree-videos")}
                            target="_blank"
                        >
                            <NavBarLinkIconV1>
                                <SubscriptionsIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1>
                                {t("label.videos")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>

                        {/* Release Notes */}
                        <NavBarLinkV1
                            externalLink
                            href={t("url.release-notes")}
                            target="_blank"
                        >
                            <NavBarLinkIconV1>
                                <ListAltIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1>
                                {t("label.release-notes")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>
                    </>
                )}

                {/* User Details */}
                <UserDialogNavBarButton />

                {/* About */}
                <AboutDialogNavBarButton />

                {/* Logout */}
                {isAuthenticated && (
                    <NavBarLinkV1
                        href={getLogoutPath()}
                        selected={isRouteCurrent(AppRoute.LOGOUT)}
                    >
                        <NavBarLinkIconV1>
                            <ExitToAppIcon />
                        </NavBarLinkIconV1>

                        <NavBarLinkTextV1>{t("label.logout")}</NavBarLinkTextV1>
                    </NavBarLinkV1>
                )}
            </NavBarSecondaryContainerV1>
        </NavBarV1>
    );
};
